/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2023 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import {
    getCurrentShipTosDataView,
    getCurrentShipToState,
    getShipToState,
} from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xScreen } from "@rc/XDL/Utilities";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const cart = getCurrentCartState(state).value;
    const shipToState = getShipToState(state, cart?.shipToId);
    return {
        cart,
        isCustomerBlock: shipToState?.value?.properties?.isCustomerBlock?.toLowerCase() !== "false",
    };
};
export interface CartExclusiveShippingMessageStyles {
    container: InjectableCss;
    messageContainer?: InjectableCss;
    textStyles: TypographyPresentationProps;
}

export const cartExclusiveShippingMessageStlyes: CartExclusiveShippingMessageStyles = {
    messageContainer: {
        css: css`
            text-align: center;
            position: relative;
            padding: 8px 16px;
            display: flex;
            justify-content: flex-start;
            align-items: center;
            background-color: ${getColor("primary")};
        `,
    },
    container: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 16px;
            margin-bottom: 11px;
            ${xScreen("lte", "md")} {
                margin-bottom: 24px;
            }
        `,
    },
    textStyles: {
        css: css`
            &&& {
                text-align: left;
                font-size: 14px;
                line-height: 22px;
                color: ${getColor("common.white")};
                padding-right: 28px;

                &,
                p:last-child {
                    margin-bottom: 0;
                }
            }
        `,
    },
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

const CartExclusiveShippingMessage: React.FC<Props> = ({ cart, isCustomerBlock }) => {
    if (!cart || !cart?.cartLines?.length) {
        return null;
    }
    return (
        <StyledWrapper {...cartExclusiveShippingMessageStlyes.container}>
            {isCustomerBlock && (
                    <StyledWrapper {...cartExclusiveShippingMessageStlyes.messageContainer}>
                        <Typography {...cartExclusiveShippingMessageStlyes.textStyles}>
                            {siteMessage("Custom_Cart_BlockedCustomerMessage")}
                        </Typography>
                    </StyledWrapper>
                )}
            {!isCustomerBlock &&
                cart.properties.calculateOrderServiceFailure === "true" && (
                    <StyledWrapper {...cartExclusiveShippingMessageStlyes.messageContainer}>
                        <Typography {...cartExclusiveShippingMessageStlyes.textStyles}>
                            {siteMessage("Custom_Cart_OrderCalculationAPIFailureMessage")}
                        </Typography>
                    </StyledWrapper>
                )}
            {cart?.properties.maxQuantityValue && (
                <StyledWrapper {...cartExclusiveShippingMessageStlyes.messageContainer}>
                    <Typography {...cartExclusiveShippingMessageStlyes.textStyles}>
                        {cart.properties.maxQuantityValue}
                    </Typography>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const definition: WidgetDefinition = {
    group: "Cart",
    displayName: "Cart Exclusive Shipping Message",
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(CartExclusiveShippingMessage),
    definition,
};

export default widgetModule;
